<template>
  <lila-default-template>
    <template #mainContent>
      <div class="container network-page">
        <!-- POC Cuidados Paliativos (Tela 7): painel da equipe matricial (role 2).
             Mostra só a própria equipe e as equipes assistenciais da sua macrorregião. -->
        <div class="nw-team">
          <div>
            <h1 class="nw-team-name">
              {{ data.team.name }}
            </h1>
            <p class="nw-team-sub">
              {{ data.team.region ? `${data.team.region} · ` : "" }}{{ data.team.referenceMonth }}
            </p>
          </div>
        </div>

        <p
          v-if="loadError"
          class="nw-error"
          role="alert"
        >
          {{ loadError }}
        </p>

        <!-- Abas: painel (Tela 7) e rede de matriciamento (Tela 9) -->
        <div
          class="nw-view-switch"
          role="tablist"
          aria-label="Visão da rede de CP"
        >
          <button
            v-for="option in views"
            :key="option.key"
            type="button"
            role="tab"
            class="nw-view-option"
            :class="{ 'is-active': view === option.key }"
            :aria-selected="view === option.key"
            @click="changeView(option.key)"
          >
            {{ option.label }}
          </button>
        </div>

        <matrix-support-network v-if="view === 'matriciamento'" />

        <template v-else>
          <!-- Alertas de conformidade (Art. 34) -->
          <section class="nw-section">
            <h2 class="nw-section-title">
              Alertas de conformidade
              <span class="nw-section-sub">Art. 34 da PNCP</span>
            </h2>

            <div
              v-if="alerts.length > 0"
              class="nw-alerts"
            >
              <article
                v-for="alert in alerts"
                :key="alert.key"
                class="nw-card nw-alert"
                :class="`is-${alert.tone}`"
              >
                <span
                  class="nw-status"
                  :class="`is-${alert.tone}`"
                >
                  <span aria-hidden="true">{{ statusIcon(alert.tone) }}</span>
                  {{ alert.label }}
                </span>
                <p class="nw-alert-title">
                  {{ alert.title }}
                </p>
                <p class="nw-alert-text">
                  {{ alert.text }}
                </p>
              </article>
            </div>
            <p
              v-else
              class="nw-empty"
            >
              Nenhuma equipe em risco neste mês.
            </p>
          </section>

          <!-- Indicadores da macro -->
          <section class="nw-section">
            <h2 class="nw-section-title">
              Rede de cuidados paliativos na macro
              <span class="nw-section-sub">{{ data.team.referenceMonth }}</span>
            </h2>

            <div class="nw-tiles">
              <div class="nw-card nw-tile">
                <p class="nw-tile-value">
                  {{ enabledTotal }} <span class="nw-tile-of">de {{ expectedTotal }}</span>
                </p>
                <p class="nw-tile-label">
                  equipes assistenciais habilitadas
                </p>
                <p class="nw-tile-note">
                  Previstas pela PNCP: 1 a cada 400 leitos SUS
                </p>
              </div>

              <div class="nw-card nw-tile">
                <p class="nw-tile-value">
                  {{ fmt(data.indicators.peopleInCp) }}
                </p>
                <p class="nw-tile-label">
                  pessoas em cuidados paliativos acompanhadas
                </p>
              </div>

              <div class="nw-card nw-tile">
                <p class="nw-tile-value">
                  {{ data.indicators.matrixSupports }}
                </p>
                <p class="nw-tile-label">
                  matriciamentos no mês
                </p>
                <p class="nw-tile-note">
                  Resposta média em {{ data.indicators.averageResponseDays }} dia
                  <template v-if="pendingRequests > 0">
                    · {{ pendingRequests }} {{ pendingRequests === 1 ? "pedido aguardando" : "pedidos aguardando" }}
                  </template>
                </p>
              </div>

              <div class="nw-card nw-tile">
                <p class="nw-tile-value">
                  {{ pct(data.indicators.withCarePlanPct) }}
                </p>
                <p class="nw-tile-label">
                  em cuidados paliativos com plano de cuidados registrado
                </p>
                <p class="nw-tile-note">
                  {{ pct(data.indicators.withDirectivesPct) }} em cuidados paliativos com diretivas antecipadas
                </p>
              </div>

              <div class="nw-card nw-tile">
                <p class="nw-tile-value">
                  {{ pct(data.indicators.deathsWithGriefPct) }}
                </p>
                <p class="nw-tile-label">
                  dos óbitos com acompanhamento de luto
                </p>
              </div>
            </div>
          </section>

          <!-- Cobertura e produção -->
          <section class="nw-section">
            <div class="nw-two-cols">
              <!-- Equipes assistenciais habilitadas x previstas -->
              <div class="nw-card">
                <header class="nw-chart-header">
                  <p class="nw-chart-headline">
                    Equipes assistenciais: habilitadas x previstas
                  </p>
                  <p class="nw-chart-legend">
                    <span
                      class="nw-unit is-on"
                      aria-hidden="true"
                    /> habilitada
                    <span
                      class="nw-unit"
                      aria-hidden="true"
                    /> prevista, ainda sem equipe
                  </p>
                </header>

                <ul class="nw-coverage">
                  <li
                    v-for="row in data.coverage"
                    :key="row.municipality"
                    class="nw-coverage-row"
                  >
                    <span class="nw-coverage-name">{{ row.municipality }}</span>
                    <span
                      class="nw-units"
                      role="img"
                      :aria-label="`${row.municipality}: ${row.enabled} de ${row.expected} equipes`"
                    >
                      <span
                        v-for="n in row.expected"
                        :key="n"
                        class="nw-unit"
                        :class="{ 'is-on': n <= row.enabled }"
                      />
                    </span>
                    <span
                      class="nw-coverage-count"
                      :class="{ 'is-short': row.enabled < row.expected }"
                    >{{ row.enabled }} de {{ row.expected }}</span>
                  </li>
                </ul>
              </div>

              <!-- Produção da própria equipe matricial -->
              <div class="nw-card">
                <header class="nw-chart-header">
                  <p class="nw-chart-headline nw-goal-headline">
                    <strong>{{ currentProduction.value }}</strong> apoios matriciais registrados em
                    {{ data.team.referenceMonth.split("/")[0].toLowerCase() }}
                  </p>
                  <span
                    class="nw-status"
                    :class="currentProduction.value >= goal ? 'is-good' : 'is-warning'"
                  >
                    <span aria-hidden="true">{{ currentProduction.value >= goal ? "✓" : "!" }}</span>
                    {{ currentProduction.value >= goal ? "Meta atingida" : "Abaixo da meta" }}
                  </span>
                </header>

                <div
                  class="nw-bars"
                  role="img"
                  :aria-label="productionAriaLabel"
                >
                  <div
                    class="nw-goal-line"
                    :style="{ bottom: `${pctOfMax(goal)}%` }"
                  >
                    <span>Meta mensal: {{ goal }}</span>
                  </div>
                  <div
                    v-for="(item, index) in data.production"
                    :key="item.month"
                    class="nw-bar-slot"
                    @mouseenter="hoverBar = index"
                    @mouseleave="hoverBar = null"
                  >
                    <span
                      v-if="hoverBar === index || index === data.production.length - 1"
                      class="nw-bar-label"
                      :style="{ bottom: `calc(${pctOfMax(item.value)}% + 6px)` }"
                    >{{ item.value }}</span>
                    <div
                      class="nw-bar"
                      :class="{ 'is-hover': hoverBar === index }"
                      :style="{ height: `${pctOfMax(item.value)}%` }"
                    />
                  </div>
                </div>
                <div class="nw-bar-axis">
                  <span
                    v-for="item in data.production"
                    :key="item.month"
                  >{{ item.month }}</span>
                </div>
                <p class="nw-chart-note">
                  Consultoria (03.11.01.007-0) e interconsulta (03.11.01.005-9) em CP, lançadas pela equipe matricial.
                </p>
                <router-link
                  v-if="ownTeamId"
                  :to="{ name: 'palliativeTeamDetail', params: { id: ownTeamId } }"
                  class="nw-team-link nw-own-link"
                >
                  Ver composição e certificações da sua equipe →
                </router-link>
              </div>
            </div>
          </section>

          <!-- Equipes assistenciais apoiadas -->
          <section class="nw-section">
            <h2 class="nw-section-title">
              Equipes assistenciais apoiadas
              <span class="nw-section-sub">mínimo de {{ goal }} procedimentos/mês e registro a cada {{ data.rules.maxDaysWithoutRecord }} dias</span>
            </h2>

            <div class="nw-card nw-table-card">
              <table class="nw-table">
                <thead>
                  <tr>
                    <th>Equipe</th>
                    <th class="is-number">
                      Procedimentos no mês
                    </th>
                    <th class="is-number">
                      Último registro
                    </th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="team in teamsWithStatus"
                    :key="team.name"
                  >
                    <td>
                      <router-link
                        :to="{ name: 'palliativeTeamDetail', params: { id: team.id } }"
                        class="nw-team-cell nw-team-link"
                      >
                        {{ team.name }}
                      </router-link>
                      <span class="nw-muted">{{ team.municipality }}</span>
                    </td>
                    <td class="is-number">
                      <span :class="{ 'is-short': team.production < goal }">{{ fmt(team.production) }}</span>
                    </td>
                    <td class="is-number">
                      <template v-if="team.daysSinceRecord == null">
                        sem registro
                      </template>
                      <template v-else>
                        há {{ team.daysSinceRecord }} {{ team.daysSinceRecord === 1 ? "dia" : "dias" }}
                      </template>
                    </td>
                    <td>
                      <span
                        class="nw-status"
                        :class="`is-${team.status.tone}`"
                      >
                        <span aria-hidden="true">{{ statusIcon(team.status.tone) }}</span>
                        {{ team.status.label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import MatrixSupportNetwork from "@components/management/MatrixSupportNetwork.vue";
import { FUNDING, getNetworkDashboard } from "@/services/palliative/networkApi";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

const emptyDashboard = () => ({
  team: { id: null, name: "", region: "", referenceMonth: "" },
  funding: FUNDING,
  rules: { minimumPerMonth: 40, maxDaysWithoutRecord: 45 },
  coverage: [],
  assistencialTeams: [],
  indicators: {
    peopleInCp: 0,
    withCarePlanPct: null,
    withDirectivesPct: null,
    deathsWithGriefPct: null,
    matrixSupports: 0,
    pendingRequests: 0,
    averageResponseDays: "—"
  },
  production: []
});

export default {
  name: "PalliativeNetwork",
  components: {
    "lila-default-template": DefaultTemplate,
    "matrix-support-network": MatrixSupportNetwork
  },
  // Pedidos de apoio e produção novos entram sem recarregar a página.
  mixins: [pocLiveSync("reload")],
  data() {
    return {
      views: [
        { key: "painel", label: "Painel" },
        { key: "matriciamento", label: "Rede de matriciamento" }
      ],
      view: this.$route.query.visao === "matriciamento" ? "matriciamento" : "painel",
      data: emptyDashboard(),
      loadError: "",
      hoverBar: null
    };
  },
  computed: {
    ownTeamId() {
      return this.data.team.id;
    },
    goal() {
      return this.data.rules.minimumPerMonth;
    },
    expectedTotal() {
      return this.data.coverage.reduce((sum, row) => sum + row.expected, 0);
    },
    enabledTotal() {
      return this.data.coverage.reduce((sum, row) => sum + row.enabled, 0);
    },
    pendingRequests() {
      return this.data.indicators.pendingRequests;
    },
    // A situação de cada equipe (art. 34) vem calculada da API.
    teamsWithStatus() {
      return [...this.data.assistencialTeams].sort((a, b) => b.status.rank - a.status.rank);
    },
    alerts() {
      const alerts = this.teamsWithStatus
        .filter((team) => team.status.atRisk)
        .map((team) => ({
          key: `team-${team.id}`,
          tone: "critical",
          label: team.status.label,
          title: `Equipe assistencial do ${team.name}`,
          text: team.status.reason,
          amount: this.data.funding.assistencial
        }));

      if (this.currentProduction.value < this.goal) {
        alerts.push({
          key: "own-team",
          tone: "critical",
          label: "Abaixo do mínimo",
          title: "Sua equipe matricial",
          text: `${this.currentProduction.value} de ${this.goal} procedimentos no mês.`,
          amount: this.data.funding.matrix
        });
      }
      return alerts;
    },
    currentProduction() {
      return this.data.production[this.data.production.length - 1] || { month: "", value: 0 };
    },
    chartMax() {
      const values = this.data.production.map((p) => p.value);
      return Math.ceil((Math.max(this.goal, ...values) * 1.15) / 10) * 10;
    },
    productionAriaLabel() {
      const values = this.data.production.map((p) => `${p.month}: ${p.value}`).join(", ");
      return `Apoios matriciais por mês contra a meta de ${this.goal}. ${values}.`;
    }
  },
  created() {
    this.reload();
  },
  methods: {
    async reload() {
      try {
        this.data = await getNetworkDashboard();
        this.loadError = "";
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar a Rede de CP.", error);
        this.loadError = error.message;
      }
    },
    // A aba fica na URL (?visao=matriciamento): o "voltar" do detalhe da equipe volta para ela.
    changeView(view) {
      this.view = view;
      this.$router.replace({ query: view === "painel" ? {} : { visao: view } });
    },
    pct(value) {
      return value == null ? "—" : `${value}%`;
    },
    statusIcon(tone) {
      return { critical: "!!", warning: "!", good: "✓" }[tone] || "";
    },
    pctOfMax(value) {
      return (value / this.chartMax) * 100;
    },
    fmt(value) {
      return Number(value).toLocaleString("pt-BR");
    },
    money(value) {
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$ink: #2b2b3a;
$ink-muted: #6b6b7b;
$primary: #4f3f82;
$primary-soft: #ece9f5;
$grid: #e4e2ea;

$critical: #c0262d;
$critical-soft: #fdecec;
$warning: #9a5b00;
$warning-soft: #fff4e0;
$good: #2e7d32;
$good-soft: #e8f5e9;

.network-page {
  padding-top: 30px;
  padding-bottom: 40px;
  color: $ink;
}

.nw-card {
  padding: 20px 25px;
  border-radius: 15px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

/* Identificação */
.nw-team {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}

.nw-team-name {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: $primary;
}

.nw-team-sub {
  font-size: 14px;
  line-height: 20px;
  color: $ink-muted;
}

.nw-error {
  margin: 0 0 16px;
  color: #db466f;
  font-size: 13px;
}

.nw-mock-badge {
  padding: 4px 12px;
  border: 1px dashed $ink-muted;
  border-radius: 999px;
  font-size: 12px;
  color: $ink-muted;
  white-space: nowrap;
}

/* Abas */
.nw-view-switch {
  display: inline-flex;
  margin-bottom: 28px;
  padding: 4px;
  border-radius: 999px;
  background-color: $primary-soft;
}

.nw-view-option {
  padding: 8px 20px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  cursor: pointer;

  &.is-active {
    color: #ffffff;
    background-color: $primary;
  }
}

/* Seções */
.nw-section {
  margin-bottom: 32px;
}

.nw-section-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
  color: $ink;
}

.nw-section-sub {
  margin-left: 6px;
  font-size: 13px;
  font-weight: 400;
  color: $ink-muted;
}

.nw-empty {
  font-size: 14px;
  color: $ink-muted;
}

/* Status: sempre ícone + rótulo */
.nw-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;

  &.is-good {
    color: $good;
    background-color: $good-soft;
  }

  &.is-warning {
    color: $warning;
    background-color: $warning-soft;
  }

  &.is-critical {
    color: $critical;
    background-color: $critical-soft;
  }
}

/* Alertas */
.nw-alerts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.nw-alert {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  border-top: 4px solid $critical;
}

.nw-alert-title {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 700;
}

.nw-alert-text {
  font-size: 14px;
  line-height: 20px;
}

.nw-alert-money {
  margin-top: 4px;
  font-size: 13px;
  color: $ink-muted;

  strong {
    color: $critical;
  }
}

/* Tiles */
.nw-tiles {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.nw-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.nw-tile-value {
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
}

.nw-tile-of {
  font-size: 16px;
  font-weight: 400;
  color: $ink-muted;
}

.nw-tile-label {
  font-size: 14px;
  line-height: 20px;
}

.nw-tile-note {
  margin-top: 6px !important;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

/* Gráficos */
.nw-two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.nw-chart-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 18px;
}

.nw-chart-headline {
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}

.nw-goal-headline {
  font-weight: 400;

  strong {
    font-size: 22px;
  }
}

.nw-chart-legend {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $ink-muted;

  .nw-unit:not(:first-child) {
    margin-left: 10px;
  }
}

.nw-chart-note {
  margin-top: 14px !important;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

/* Cobertura: um quadrado por equipe prevista */
.nw-coverage {
  margin: 0;
  padding: 0 !important;
  list-style: none;
}

.nw-coverage-row {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr) 60px;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid $grid;

  &:last-child {
    border-bottom: 0;
  }
}

.nw-coverage-name {
  font-size: 14px;
  font-weight: 600;
}

.nw-units {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.nw-unit {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 2px solid $primary;
  border-radius: 5px;
  background-color: #ffffff;

  &.is-on {
    background-color: $primary;
  }
}

.nw-chart-legend .nw-unit {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.nw-coverage-count {
  font-size: 14px;
  font-weight: 700;
  text-align: right;

  &.is-short {
    color: $warning;
  }
}

/* Barras: produção x meta */
.nw-bars {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 160px;
  margin-top: 24px;
  border-bottom: 1px solid $grid;
}

.nw-goal-line {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 2px dashed $ink-muted;
  z-index: 1;
  pointer-events: none;

  span {
    position: absolute;
    right: 0;
    top: -20px;
    padding: 0 4px;
    font-size: 11px;
    color: $ink-muted;
    background-color: #ffffff;
  }
}

.nw-bar-slot {
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
}

.nw-bar {
  width: 60%;
  max-width: 36px;
  border-radius: 4px 4px 0 0;
  background-color: $primary;
  transition: opacity 0.15s ease-in-out;

  &.is-hover {
    opacity: 0.8;
  }
}

.nw-bar-label {
  position: absolute;
  font-size: 12px;
  font-weight: 700;
}

.nw-bar-axis {
  display: flex;
  gap: 12px;
  margin-top: 6px;

  span {
    flex: 1;
    font-size: 12px;
    text-align: center;
    color: $ink-muted;
  }
}

/* Tabela de equipes */
.nw-table-card {
  overflow-x: auto;
}

.nw-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    padding: 0 12px 10px 0;
    border-bottom: 1px solid $grid;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    color: $ink-muted;
  }

  td {
    padding: 12px 12px 12px 0;
    border-bottom: 1px solid $grid;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .is-number {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .is-short {
    font-weight: 700;
    color: $critical;
  }
}

.nw-team-cell {
  display: block;
  font-weight: 600;
}

.nw-team-link {
  color: $primary;

  &:hover {
    text-decoration: underline;
  }
}

.nw-own-link {
  display: inline-block;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
}

.nw-muted {
  display: block;
  font-size: 12px;
  color: $ink-muted;
}

@media (max-width: 1200px) {
  .nw-tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .nw-two-cols,
  .nw-alerts {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 700px) {
  .nw-tiles {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
